import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Lock, Save, Smartphone, UserRound, UserX, Zap } from "lucide-react";
import * as Switch from "@radix-ui/react-switch";

import { getAvatarColor } from "../utils/tasks";
import { InlineIcon, UiIcon } from "./Icon";

import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import phoneInputLabels from 'react-phone-number-input/locale/pl'

export default function CreateScreen({
  userTier,
  userLimits,
  form,
  contractors,
  setForm,
  onCreateContractor,
  onSubmit,
  isSubmitting = false,
  onBack,
  isModal = false,
}) {
  const [newContractor, setNewContractor] = useState({
    name: "",
    phone: "",
    position: "",
    company: "",
  });
  const [creatingContractor, setCreatingContractor] = useState(false);
  const [contractorError, setContractorError] = useState("");
  const [showAllContractors, setShowAllContractors] = useState(false);
  const [showNewContractorForm, setShowNewContractorForm] = useState(false);
  const [submitLocked, setSubmitLocked] = useState(false);
  const submitUnlockTimeoutRef = useRef(null);

  const assignee = contractors.find((c) => c.id === form.contactId);
  const isUnassigned = form.contactId === null;
  const isSelfSelected = form.contactId === 0;
  const isContractorSelected = !!assignee && assignee.id > 0;
  const valid = form.desc.trim();

  const maxContractors = userLimits?.contractors_per_event?.max ?? null;
  const realContractorCount = contractors.filter((c) => c.id > 0).length;
  const contractorsAtLimit = maxContractors !== null && realContractorCount >= maxContractors;
  const otherContractorsCount = contractors.filter((c) => c.id > 0).length;
  const visibleContractors = showAllContractors
    ? contractors
    : contractors.filter((c) => c.id === 0);

  const hourOptions = Array.from({ length: 24 }, (_, index) =>
    index.toString().padStart(2, "0")
  );
  const minuteOptions = ["00", "15", "30", "45"];

  const splitTime = (timeValue) => {
    if (!timeValue) {
      return { hour: "", minute: "" };
    }

    const [hour, minute] = timeValue.split(":");
    return { hour: hour || "", minute: minute || "" };
  };

  const getTimeValue = (hour, minute) => {
    if (!hour && !minute) {
      return "";
    }
    return `${hour || "00"}:${minute || "00"}`;
  };

  useEffect(() => {
    if (!isContractorSelected && form.notifySms) {
      setForm((f) => ({ ...f, notifySms: false }));
    }
  }, [isContractorSelected, form.notifySms, setForm]);

  const contractorEmail = assignee?.email || "";
  const isContractorWithEmail = isContractorSelected && !!contractorEmail;

  useEffect(() => {
    if (!isContractorWithEmail && form.notifyEmail) {
      setForm((f) => ({ ...f, notifyEmail: false }));
    }
  }, [isContractorWithEmail, form.notifyEmail, setForm]);

  useEffect(() => {
    return () => {
      if (submitUnlockTimeoutRef.current) {
        clearTimeout(submitUnlockTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = () => {
    if (!valid || submitLocked) {
      return;
    }

    setSubmitLocked(true);
    if (submitUnlockTimeoutRef.current) {
      clearTimeout(submitUnlockTimeoutRef.current);
    }
    submitUnlockTimeoutRef.current = setTimeout(() => {
      setSubmitLocked(false);
      submitUnlockTimeoutRef.current = null;
    }, 5000);

    onSubmit();
  };

  const submitNewContractor = async () => {
    setContractorError("");
    if (!newContractor.name.trim()) {
      setContractorError("Podaj nazwę wykonawcy.");
      return;
    }

    setCreatingContractor(true);
    const result = await onCreateContractor(newContractor);
    setCreatingContractor(false);

    if (!result.ok) {
      if (result.error.includes("Incorrect phone number format")) {
        setContractorError("Wprowadź poprawny numer telefonu (np. +1234567890).");
        return;
      }
      setContractorError(result.error || "Nie udało się dodać wykonawcy.");
      return;
    }

    setNewContractor({ name: "", phone: "", position: "", company: "" });
  };

  return (
    <div
      style={{
        fontFamily: "var(--font-body)",
        background: "var(--color-bg-app)",
        minHeight: isModal ? "auto" : "100dvh",
        maxWidth: isModal ? "100%" : 480,
        width: isModal ? "100%" : "calc(100% - 16px)",
        margin: isModal ? 0 : "0 auto",
        border: isModal ? "1px solid var(--color-border)" : "none",
        borderRadius: isModal ? "var(--radius-lg)" : 0,
        overflow: isModal ? "hidden" : "visible",
      }}
    >
      <div
        style={{
          background: "var(--color-bg-card)",
          borderBottom: "1px solid var(--color-border)",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: "var(--color-text-muted)",
            cursor: "pointer",
            fontSize: 20,
            lineHeight: 1,
            padding: 0,
          }}
        >
          <UiIcon Icon={ArrowLeft} />
        </button>
        <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-h3)", fontWeight: "var(--font-semibold)", color: "var(--color-text-primary)" }}>
          Nowe zadanie
        </div>
        <div />
      </div>

      <div style={{ padding: 20 }}>
        {!isUnassigned && !isSelfSelected && (
          <div
            style={{
                background: "var(--color-primary-light)",
                border: "1px solid var(--color-primary-border)",
                borderRadius: "var(--radius-md)",
              padding: "10px 14px",
              marginBottom: 20,
                fontSize: "var(--text-sm)",
                color: "var(--color-primary)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <InlineIcon Icon={Smartphone} style={{ marginRight: 2 }} /> Zadanie przypisane do wykonawcy
          </div>
        )}
        {isSelfSelected && (
          <div
            style={{
                background: "var(--color-success-bg)",
                border: "1px solid color-mix(in srgb, var(--color-success) 28%, white)",
                borderRadius: "var(--radius-md)",
              padding: "10px 14px",
              marginBottom: 20,
                fontSize: "var(--text-sm)",
              color: "var(--color-success)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <InlineIcon Icon={UserRound} style={{ marginRight: 2 }} /> Zadanie przypisane do Ciebie (koordynatora)
          </div>
        )}
        {isUnassigned && (
          <div
            style={{
                background: "var(--color-warning-bg)",
                border: "1px solid color-mix(in srgb, var(--color-warning) 28%, white)",
                borderRadius: "var(--radius-md)",
              padding: "10px 14px",
              marginBottom: 20,
                fontSize: "var(--text-sm)",
                color: "var(--color-warning)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <InlineIcon Icon={UserX} style={{ marginRight: 2 }} /> Zadanie bez przypisania — możesz je przypisać później
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
                color: "var(--color-text-muted)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8,
            }}
          >
            Opis zadania *
          </label>
          <textarea
            value={form.desc}
            onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))}
            placeholder="Co trzeba zrobić?"
            rows={3}
            style={{
              width: "100%",
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
              padding: "12px 14px",
                color: "var(--color-text-primary)",
                fontSize: "var(--text-body)",
              outline: "none",
              resize: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
                color: "var(--color-text-muted)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8,
            }}
          >
            Lokalizacja
          </label>
          <input
            type="text"
            value={form.location || ""}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="Np. Sala A, ul. Główna 10"
            style={{
              width: "100%",
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
              padding: "11px 14px",
                color: "var(--color-text-primary)",
                fontSize: "var(--text-body)",
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
                color: "var(--color-text-muted)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8,
            }}
          >
            Termin{" "}
            <span
              style={{
                  color: "var(--color-text-muted)",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: 0,
                fontSize: "var(--text-xs)",
              }}
            >
              (opcjonalnie)
            </span>
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              style={{
                flex: 1,
                  background: "var(--color-bg-card)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                padding: "11px 14px",
                  color: "var(--color-text-primary)",
                  fontSize: "var(--text-body)",
                outline: "none",
                fontFamily: "inherit",
              }}
            />
            <select
              value={splitTime(form.time).hour}
              onChange={(e) => {
                const current = splitTime(form.time);
                setForm((f) => ({ ...f, time: getTimeValue(e.target.value, current.minute) }));
              }}
              style={{
                flex: 1,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "11px 14px",
                color: "var(--color-text-primary)",
                fontSize: "var(--text-body)",
                outline: "none",
                fontFamily: "inherit",
              }}
            >
              <option value="">--</option>
              {hourOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <select
              value={splitTime(form.time).minute}
              onChange={(e) => {
                const current = splitTime(form.time);
                setForm((f) => ({ ...f, time: getTimeValue(current.hour, e.target.value) }));
              }}
              style={{
                flex: 1,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "11px 14px",
                color: "var(--color-text-primary)",
                fontSize: "var(--text-body)",
                outline: "none",
                fontFamily: "inherit",
              }}
            >
              <option value="">--</option>
              {minuteOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              fontSize: "var(--text-xs)",
              fontWeight: "var(--font-medium)",
              color: "var(--color-text-muted)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8,
            }}
          >
            Przypisz do{" "}
            <span
              style={{
                color: "var(--color-text-muted)",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: 0,
                fontSize: "var(--text-xs)",
              }}
            >
              (opcjonalnie)
            </span>
          </label>

          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button
              onClick={() => setForm((f) => ({ ...f, contactId: null }))}
              style={{
                flex: 1,
                padding: "10px 0",
                borderRadius: "var(--radius-md)",
                fontWeight: "var(--font-medium)",
                fontSize: "var(--text-sm)",
                cursor: "pointer",
                fontFamily: "inherit",
                background: isUnassigned ? "var(--color-warning-bg)" : "var(--color-bg-card)",
                color: isUnassigned ? "var(--color-warning)" : "var(--color-text-muted)",
                border: `1.5px solid ${isUnassigned ? "color-mix(in srgb, var(--color-warning) 28%, white)" : "var(--color-border)"}`,
              }}
            >
              <InlineIcon Icon={UserX} style={{ marginRight: 4 }} />
              Nieprzypisane
            </button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
            <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", fontWeight: "var(--font-medium)" }}>
              lub wybierz wykonawcę
            </span>
            <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {visibleContractors.map((c) => {
              const sel = form.contactId === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() =>
                    setForm((f) => ({ ...f, contactId: sel ? null : c.id }))
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: "var(--radius-md)",
                    border: `1.5px solid ${sel ? "var(--color-primary-border)" : "var(--color-border)"}`,
                    background: sel ? "var(--color-primary-light)" : "var(--color-bg-card)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: getAvatarColor(c.id),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--color-bg-card)",
                      flexShrink: 0,
                    }}
                  >
                    {c.name[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "var(--text-body)",
                        fontWeight: "var(--font-medium)",
                        color: "var(--color-text-primary)",
                      }}
                    >
                      {c.name}
                    </div>
                    <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                      {c.phone ? `${c.role} | ${c.phone}` : c.role}
                    </div>
                  </div>
                  {sel && (
                    <span style={{ color: "var(--color-primary)", display: "inline-flex" }}>
                      <UiIcon Icon={Check} />
                    </span>
                  )}
                </button>
              );
            })}
            {!showAllContractors && contractors.length > 0 && visibleContractors.length === 0 && (
              <div
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--color-text-muted)",
                  textAlign: "center",
                  padding: "8px 0",
                }}
              >
                Lista wykonawców jest zwinięta.
              </div>
            )}
            {contractors.length === 0 && (
              <div
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--color-text-muted)",
                  textAlign: "center",
                  padding: "8px 0",
                }}
              >
                Brak wykonawców dla tego wydarzenia.
              </div>
            )}
            {contractors.length > 0 && (
              <button
                type="button"
                onClick={() => setShowAllContractors((value) => !value)}
                style={{
                  marginTop: 2,
                  border: "1px solid var(--color-border)",
                  background: "var(--color-bg-card)",
                  color: "var(--color-text-muted)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                {showAllContractors ? (
                  <>
                    <InlineIcon Icon={ChevronUp} />
                    Ukryj innych wykonawców
                  </>
                ) : (
                  <>
                    <InlineIcon Icon={ChevronDown} />
                    Pokaż innych wykonawców ({otherContractorsCount})
                  </>
                )}
              </button>
            )}
          </div>

          <div
            style={{
              marginTop: 12,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              background: "var(--color-bg-card)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  color: isContractorSelected ? "var(--color-text-primary)" : "var(--color-text-muted)",
                }}
              >
                Powiadom wykonawcę SMS
              </div>
              <div
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--color-text-muted)",
                  marginTop: 2,
                }}
              >
              </div>
            </div>

            <Switch.Root
              checked={form.notifySms && isContractorSelected}
              onCheckedChange={(checked) =>
                setForm((f) => ({ ...f, notifySms: checked }))
              }
              disabled={!isContractorSelected}
              style={{
                all: "unset",
                width: 44,
                height: 24,
                minWidth: 0,
                minHeight: 0,
                background: form.notifySms && isContractorSelected
                  ? "var(--color-primary)"
                  : "var(--color-bg-subtle)",
                border: "1px solid var(--color-border)",
                borderRadius: 9999,
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                cursor: isContractorSelected ? "pointer" : "not-allowed",
                opacity: isContractorSelected ? 1 : 0.7,
                boxSizing: "border-box",
                flexShrink: 0,
                transition: "background-color 0.2s ease",
              }}
              aria-label="Przełącz powiadomienie SMS"
            >
              <Switch.Thumb
                style={{
                  display: "block",
                  width: 20,
                  height: 20,
                  background: "var(--color-bg-card)",
                  border: "1px solid color-mix(in srgb, var(--color-border) 80%, white)",
                  borderRadius: 9999,
                  boxSizing: "border-box",
                  transition: "transform 0.2s ease",
                  transform:
                    form.notifySms && isContractorSelected
                      ? "translateX(21px)"
                      : "translateX(1px)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.14)",
                  willChange: "transform",
                }}
              />
            </Switch.Root>
          </div>

          <div
            style={{
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              background: "var(--color-bg-card)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  color: isContractorWithEmail ? "var(--color-text-primary)" : "var(--color-text-muted)",
                }}
              >
                Powiadom wykonawcę e-mail
              </div>
              {isContractorSelected && !contractorEmail && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginTop: 2 }}>
                  Wykonawca nie ma adresu e-mail
                </div>
              )}
            </div>

            <Switch.Root
              checked={form.notifyEmail && isContractorWithEmail}
              onCheckedChange={(checked) =>
                setForm((f) => ({ ...f, notifyEmail: checked }))
              }
              disabled={!isContractorWithEmail}
              style={{
                all: "unset",
                width: 44,
                height: 24,
                minWidth: 0,
                minHeight: 0,
                background: form.notifyEmail && isContractorWithEmail
                  ? "var(--color-primary)"
                  : "var(--color-bg-subtle)",
                border: "1px solid var(--color-border)",
                borderRadius: 9999,
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                cursor: isContractorWithEmail ? "pointer" : "not-allowed",
                opacity: isContractorWithEmail ? 1 : 0.7,
                boxSizing: "border-box",
                flexShrink: 0,
                transition: "background-color 0.2s ease",
              }}
              aria-label="Przełącz powiadomienie e-mail"
            >
              <Switch.Thumb
                style={{
                  display: "block",
                  width: 20,
                  height: 20,
                  background: "var(--color-bg-card)",
                  border: "1px solid color-mix(in srgb, var(--color-border) 80%, white)",
                  borderRadius: 9999,
                  boxSizing: "border-box",
                  transition: "transform 0.2s ease",
                  transform:
                    form.notifyEmail && isContractorWithEmail
                      ? "translateX(21px)"
                      : "translateX(1px)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.14)",
                  willChange: "transform",
                }}
              />
            </Switch.Root>
          </div>

          <button
            type="button"
            onClick={() => setShowNewContractorForm((value) => !value)}
            style={{
              marginTop: 12,
              width: "100%",
              border: "1px solid var(--color-border)",
              background: "var(--color-bg-card)",
              color: "var(--color-text-muted)",
              borderRadius: "var(--radius-md)",
              padding: "9px 10px",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              cursor: "pointer",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            {showNewContractorForm ? (
              <>
                <InlineIcon Icon={ChevronUp} />
                Ukryj formularz wykonawcy
              </>
            ) : (
              <>
                <InlineIcon Icon={ChevronDown} />
                Dodaj nowego wykonawcę
              </>
            )}
          </button>

          {showNewContractorForm && (
            <div
              style={{
                marginTop: 8,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: 12,
                display: "grid",
                gap: 8,
              }}
            >
              {contractorError && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-danger)" }}>
                  {contractorError}
                </div>
              )}
              <input
                placeholder="Nazwa *"
                value={newContractor.name}
                onChange={(e) =>
                  setNewContractor((c) => ({ ...c, name: e.target.value }))
                }
                style={{
                  width: "100%",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                  boxSizing: "border-box",
                }}
              />
              <PhoneInput
                defaultCountry="PL"
                international
                displayInitialValueAsLocal
                countryCallingCodeEditable={false}
                value={newContractor.phone || undefined}
                onChange={(value) =>
                  setNewContractor((c) => ({ ...c, phone: value || "" }))
                }
                labels={phoneInputLabels}
                numberInputProps={{
                  placeholder: "Telefon",
                  style: {
                    border: "none",
                    outline: "none",
                    width: "100%",
                    minWidth: 0,
                    background: "transparent",
                  },
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                  boxSizing: "border-box"
                }}
              />
              <input
                placeholder="Rola"
                value={newContractor.position}
                onChange={(e) =>
                  setNewContractor((c) => ({ ...c, position: e.target.value }))
                }
                style={{
                  width: "100%",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                  boxSizing: "border-box",
                }}
              />
              <input
                placeholder="Firma"
                value={newContractor.company}
                onChange={(e) =>
                  setNewContractor((c) => ({ ...c, company: e.target.value }))
                }
                style={{
                  width: "100%",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={contractorsAtLimit ? undefined : submitNewContractor}
                disabled={creatingContractor || contractorsAtLimit}
                style={{
                  border: contractorsAtLimit
                    ? "1px solid var(--color-border)"
                    : "1px solid var(--color-primary-border)",
                  background: contractorsAtLimit
                    ? "var(--color-bg-app)"
                    : "var(--color-primary-light)",
                  color: contractorsAtLimit
                    ? "var(--color-text-muted)"
                    : "var(--color-primary)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  cursor: (contractorsAtLimit || creatingContractor) ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                {contractorsAtLimit
                  ? <><InlineIcon Icon={Lock} style={{ marginRight: 2 }} />Limit wykonawców osiągnięty</>
                  : creatingContractor ? "Dodawanie..." : "+ Dodaj wykonawcę"}
              </button>
              {contractorsAtLimit && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginTop: 2 }}>
                  Osiągnięto limit {maxContractors} wykonawców dla tego planu. Przejdź na wyższy plan, aby dodać więcej.
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 28 }}>
          <label
            style={{
              fontSize: "var(--text-xs)",
              fontWeight: "var(--font-medium)",
              color: "var(--color-text-muted)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8,
            }}
          >
            Priorytet
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            {[
              { k: "normal", l: "Normalny" },
              { k: "high", l: "Pilne" },
            ].map((p) => (
              <button
                key={p.k}
                onClick={() => setForm((f) => ({ ...f, priority: p.k }))}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  borderRadius: 10,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  background:
                    form.priority === p.k
                      ? p.k === "high"
                        ? "var(--color-warning-bg)"
                        : "var(--color-primary-light)"
                      : "var(--color-bg-card)",
                  color:
                    form.priority === p.k
                      ? p.k === "high"
                        ? "var(--color-warning)"
                        : "var(--color-primary)"
                      : "var(--color-text-muted)",
                  border: `1.5px solid ${
                    form.priority === p.k
                      ? p.k === "high"
                        ? "var(--color-warning-bg)"
                        : "var(--color-primary-border)"
                      : "var(--color-border)"
                  }`,
                }}
              >
                {p.k === "high" ? (
                  <>
                    <InlineIcon Icon={Zap} style={{ marginRight: 4 }} />
                    {p.l}
                  </>
                ) : (
                  p.l
                )}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={!valid || submitLocked}
          style={{
            width: "100%",
            padding: 16,
            borderRadius: "var(--radius-lg)",
            fontWeight: "var(--font-medium)",
            fontSize: "var(--text-body)",
            cursor: valid && !submitLocked ? "pointer" : "default",
            border: "none",
            background: valid && !submitLocked ? "var(--color-primary)" : "var(--color-bg-hover)",
            color: valid && !submitLocked ? "var(--color-bg-card)" : "var(--color-text-muted)",
            fontFamily: "inherit",
          }}
        >
          {submitLocked || isSubmitting
            ? "Zapisywanie..."
            : !valid
            ? "Uzupełnij opis zadania"
            : isUnassigned
            ? (
              <>
                <InlineIcon Icon={Save} style={{ marginRight: 4 }} />
                Zapisz zadanie (nieprzypisane)
              </>
            )
            : assignee
            ? `Wyślij zadanie do ${assignee.name}`
            : (
              <>
                <InlineIcon Icon={Save} style={{ marginRight: 4 }} />
                Zapisz zadanie
              </>
            )}
        </button>
      </div>
    </div>
  );
}
